import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import {
  FiSearch,
  FiFilter,
  FiUser,
  FiArrowLeft,
  FiMessageSquare,
  FiChevronLeft,
  FiChevronRight,
  FiDownload,
} from "react-icons/fi";
import {
  BsWhatsapp,
  BsThreeDotsVertical,
  BsCheck2All,
  BsExclamationCircle,
  BsCheck,
} from "react-icons/bs";
import { FaRegFileAlt, FaImage, FaVideo } from "react-icons/fa";
import apiEndpoints from "../../apiconfig";

// Chat List Component
const ChatList = React.memo(
  ({
    wabaNumber,
    searchQuery,
    setSearchQuery,
    filteredChats,
    selectedChat,
    handleChatSelect,
    formatTime,
    chatListRef,
    handleChatScroll,
    formatChatListTime,
    loadingChats,
    filter,
    setFilter,
  }) => (
    <div className="w-full lg:w-96 bg-white border-r border-gray-200 flex flex-col h-full font-['Montserrat'] shadow-sm">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1f8c55] to-[#25D366] text-white px-6 py-5">
        <div className="flex justify-between items-center mb-2">
          <h1 className="text-xl font-semibold font-['Montserrat']">
            Business Inbox
          </h1>
          <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full">
            <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
            <span className="text-xs font-medium font-['Montserrat']">
              LIVE
            </span>
          </div>
        </div>
        <p className="text-white/80 text-sm font-['Montserrat']">
          WABA: {wabaNumber}
        </p>
      </div>

      {/* Search & Filter Bar */}
      <div className="p-4 bg-gray-50/80 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider font-['Montserrat']">
            Recent Chats
          </span>
          <button
            onClick={() => setFilter(filter === "all" ? "inbound" : "all")}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all border text-xs font-semibold uppercase tracking-wider ${filter === "inbound"
              ? "bg-[#25D366] text-white border-[#25D366] shadow-sm"
              : "bg-white text-gray-600 border-gray-200 hover:bg-gray-100"
              }`}
            title={filter === "inbound" ? "Showing received chats" : "Filter by received"}
          >
            <FiFilter className={filter === "inbound" ? "text-white" : "text-gray-400"} />
            <span>
              {filter === "inbound" ? "Inbound" : "Filter"}
            </span>
          </button>
        </div>
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
          <input
            type="text"
            placeholder="Search ..."
            className="w-full pl-10 pr-10 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#25D366] focus:border-transparent outline-none transition-all font-['Montserrat']"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Chat List */}
      <div
        ref={chatListRef}
        onScroll={handleChatScroll}
        className="flex-1 overflow-y-auto bg-white"
      >
        {loadingChats && filteredChats.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-10 h-10 border-4 border-[#25D366] border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm font-medium text-gray-500 mt-4 font-['Montserrat']">
              Loading conversations...
            </p>
          </div>
        ) : filteredChats.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-gray-400 py-12">
            <FiMessageSquare className="text-4xl mb-3 opacity-50" />
            <p className="text-sm font-medium font-['Montserrat']">
              No conversations found
            </p>
          </div>
        ) : (
          <>
            {filteredChats.map((chat) => (
              <div
                key={chat.id}
                onClick={() => handleChatSelect(chat)}
                className={`p-4 cursor-pointer border-b border-gray-100 transition-all duration-200 ${selectedChat?.id === chat.id
                  ? "bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-[#25D366]"
                  : "hover:bg-gray-50"
                  }`}
              >
                <div className="flex items-start space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#25D366] to-[#1f8c55] rounded-full flex items-center justify-center flex-shrink-0">
                    <FiUser className="text-white text-lg" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start mb-1">
                      <h3 className="font-semibold text-gray-900 truncate text-sm font-['Montserrat']">
                        {chat.contact_name || chat.mobile_number}
                      </h3>
                      <span className="text-xs text-gray-500 whitespace-nowrap ml-2 font-['Montserrat']">
                        {chat.last_message_time
                          ? formatChatListTime(chat.last_message_time)
                          : ""}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 truncate leading-relaxed font-['Montserrat']">
                      {chat.last_message || "Start a conversation"}
                    </p>
                  </div>
                </div>
              </div>
            ))}
            {loadingChats && (
              <div className="flex justify-center py-4">
                <div className="w-6 h-6 border-2 border-[#25D366] border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  ),
);

const HistoryLive = () => {
  const [chats, setChats] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isMobileView, setIsMobileView] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const messagesContainerRef = useRef(null);
  const chatListRef = useRef(null);
  const isInitialLoadRef = useRef(false);
  const [chatPage, setChatPage] = useState(1);
  const [chatHasMore, setChatHasMore] = useState(true);
  const [loadingChats, setLoadingChats] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const activeChatRef = useRef(null);
  const isFetchingNextPageRef = useRef(false);
  const isAtBottomRef = useRef(true);
  const [wabaNumber, setWabaNumber] = useState("");
  const prevScrollHeightRef = useRef(0);
  const isPrependingRef = useRef(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingOld, setLoadingOld] = useState(false);
  const shouldAutoScrollRef = useRef(true); // New ref to control auto-scroll
  const [filter, setFilter] = useState("all"); // 'all' or 'inbound'

  // Responsive screen detection
  // useEffect(() => {
  //   if (debouncedQuery) return;
  //   const checkScreen = () => {
  //     const mobile = window.innerWidth < 1024;
  //     setIsMobileView(mobile);
  //     if (!mobile) setShowChat(false);
  //   };

  //   checkScreen();
  //   window.addEventListener("resize", checkScreen);

  //   // ✅ Load WABA number from localStorage safely
  //   const storedWaba = localStorage.getItem("waba_number");
  //   if (storedWaba) {
  //     setWabaNumber(storedWaba);
  //   }

  //   // ✅ Fetch chat list
  //   const token = localStorage.getItem("token");
  //   setChats([]); // Clear current chats to show loading state for entire list
  //   setLoadingChats(true);

  //   fetch(`${apiEndpoints.chat_history}?filter=${filter}`, {
  //     method: "GET",
  //     headers: {
  //       Authorization: `Bearer ${token}`,
  //     },
  //   })
  //     .then((res) => res.json())
  //     .then((data) => {
  //       if (data.status && Array.isArray(data.data)) {
  //         setChats(data.data);
  //         setChatPage(1);
  //         setChatHasMore(true);
  //       }
  //     })
  //     .catch((err) => {
  //       console.error("Fetch error:", err);
  //     })
  //     .finally(() => {
  //       setLoadingChats(false);
  //     });

  //   return () => window.removeEventListener("resize", checkScreen);
  // }, [filter,debouncedQuery]);

  // ✅ Select chat & load messages
  const handleChatSelect = async (chat) => {
    setSelectedChat(chat);
    setMessages([]);
    setPage(1);
    setHasMore(true);
    setLoadingOld(false);
    setLoadingMessages(true);
    shouldAutoScrollRef.current = true; // Enable auto-scroll for new chat
    isInitialLoadRef.current = true;

    activeChatRef.current = chat.mobile_number;
    const token = localStorage.getItem("token");

    try {
      const res = await fetch(
        `${apiEndpoints.chat_messages}?mobile_number=${chat.mobile_number}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const data = await res.json();

      // ⛔ ignore stale response
      if (activeChatRef.current !== chat.mobile_number) return;

      if (data.status && Array.isArray(data.data)) {
        setMessages(data.data);
      } else {
        setMessages([]);
      }
    } catch (err) {
      console.error("Message fetch error:", err);
      setMessages([]);
    } finally {
      // ✅ ALWAYS stop loader
      if (activeChatRef.current === chat.mobile_number) {
        setLoadingMessages(false);
      }
    }

    if (isMobileView) setShowChat(true);
  };

  // Auto-scroll to bottom on new messages or initial load
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    // Only auto-scroll if we're supposed to
    if (shouldAutoScrollRef.current) {
      requestAnimationFrame(() => {
        container.scrollTop = container.scrollHeight;
      });
    }
  }, [messages]);

  const handleBackToChatList = () => {
    setShowChat(false);
    setSelectedChat(null);
    setMessages([]);
  };

  useEffect(() => {
    const delay = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 1000); // 300ms debounce

    return () => clearTimeout(delay);
  }, [searchQuery]);

useEffect(() => {
  const token = localStorage.getItem("token");

  setLoadingChats(true);
  setChats([]);

  const url = debouncedQuery
    ? `${apiEndpoints.chat_history}?search=${debouncedQuery}&filter=${filter}`
    : `${apiEndpoints.chat_history}?filter=${filter}`;

  fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => res.json())
    .then((data) => {
      if (data.status && Array.isArray(data.data)) {
        setChats(data.data);
        setChatPage(1);
        setChatHasMore(true);
      }
    })
    .catch((err) => console.error(err))
    .finally(() => setLoadingChats(false));
}, [debouncedQuery, filter]);

  // Filter chats based on search
  const filteredChats = chats;

  // Format timestamp
  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatChatListTime = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const isToday =
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();

    if (isToday) {
      return formatTime(timestamp);
    } else {
      return (
        date.toLocaleDateString([], {
          day: "2-digit",
          month: "short",
        }) +
        " " +
        formatTime(timestamp)
      );
    }
  };

  const getDateLabel = (dateStr) => {
    const msgDate = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const isSameDay = (d1, d2) =>
      d1.getDate() === d2.getDate() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getFullYear() === d2.getFullYear();

    if (isSameDay(msgDate, today)) return "Today";
    if (isSameDay(msgDate, yesterday)) return "Yesterday";

    return msgDate.toLocaleDateString(undefined, {
      weekday: "long",
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const handleChatScroll = async () => {
    const container = chatListRef.current;
    if (!container) return;

    // 🚫 if list doesn't overflow, stop
    if (container.scrollHeight <= container.clientHeight) return;

    // 🚫 already fetching or no more data
    if (loadingChats || !chatHasMore || isFetchingNextPageRef.current) return;

    const threshold = 20;

    if (
      container.scrollTop + container.clientHeight >=
      container.scrollHeight - threshold
    ) {
      isFetchingNextPageRef.current = true;
      setLoadingChats(true);

      const prevScrollHeight = container.scrollHeight;
      const prevScrollTop = container.scrollTop;

      const nextPage = chatPage + 1;
      const token = localStorage.getItem("token");

      try {
        const res = await fetch(
          `${apiEndpoints.chat_history}?page=${nextPage}&filter=${filter}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        const data = await res.json();

        if (data.status && data.data.length > 0) {
          setChats((prev) => {
            const map = new Map(prev.map((c) => [c.mobile_number, c]));
            data.data.forEach((c) => map.set(c.mobile_number, c));
            return Array.from(map.values());
          });

          setChatPage(nextPage);
        } else {
          // 🔒 HARD STOP (VERY IMPORTANT)
          setChatHasMore(false);
        }
      } catch (err) {
        console.error("Chat pagination error:", err);
      } finally {
        setLoadingChats(false);
        setTimeout(() => {
          isFetchingNextPageRef.current = false;
        }, 300);
      }
    }
  };

  const handleScroll = async () => {
    const container = messagesContainerRef.current;
    if (!container || loadingOld || !hasMore) return;

    // Calculate scroll position
    const scrollTop = container.scrollTop;
    const clientHeight = container.clientHeight;
    const scrollHeight = container.scrollHeight;

    // Check if user is at the bottom (within 5px)
    const isAtBottom = Math.abs(scrollHeight - (scrollTop + clientHeight)) < 5;
    isAtBottomRef.current = isAtBottom;

    // Disable auto-scroll if user manually scrolls up
    if (!isAtBottom) {
      shouldAutoScrollRef.current = false;
    } else {
      shouldAutoScrollRef.current = true;
    }

    // 🔥 Only trigger when user reaches TOP (loading older messages)
    if (scrollTop <= 5) {
      setLoadingOld(true);
      prevScrollHeightRef.current = container.scrollHeight;
      isPrependingRef.current = true;
      shouldAutoScrollRef.current = false; // Disable auto-scroll when loading older messages

      const nextPage = page + 1;
      const token = localStorage.getItem("token");

      try {
        const res = await fetch(
          `${apiEndpoints.chat_messages}?mobile_number=${selectedChat.mobile_number}&page=${nextPage}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        const data = await res.json();

        if (data.status && data.data.length > 0) {
          setMessages((prev) => [...data.data, ...prev]);
          setPage(nextPage);
        } else {
          setHasMore(false);
        }
      } catch (err) {
        console.error("Message pagination error:", err);
      } finally {
        setLoadingOld(false);
      }
    }
  };

  useLayoutEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    // 🔥 ONLY when older messages were prepended
    if (isPrependingRef.current) {
      const newScrollHeight = container.scrollHeight;
      const prevScrollHeight = prevScrollHeightRef.current;

      // Maintain scroll position when loading older messages
      container.scrollTop = newScrollHeight - prevScrollHeight;

      isPrependingRef.current = false;
    }
  }, [messages]);

  const groupedMessages = messages.reduce((acc, msg) => {
    const dateKey = new Date(msg.timestamp).toDateString();
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(msg);
    return acc;
  }, {});

  const getMediaType = (msg) => {
    // 1️⃣ Prefer mime_type if present
    if (msg.mime_type) {
      if (msg.mime_type.startsWith("image")) return "image";
      if (msg.mime_type.startsWith("video")) return "video";
      if (msg.mime_type.startsWith("audio")) return "audio";
    }

    // 2️⃣ Fallback: detect from file extension
    if (msg.media_url) {
      const url = msg.media_url.toLowerCase();
      if (url.match(/\.(jpg|jpeg|png|gif|webp)$/)) return "image";
      if (url.match(/\.(mp4|webm|ogg)$/)) return "video";
      if (url.match(/\.(mp3|wav|ogg)$/)) return "audio";
    }

    return "document";
  };

  const renderMediaMessage = (msg) => {
    if (!msg.media_url) return null;

    const type = getMediaType(msg);

    const downloadLink = (
      <a
        href={msg.media_url}
        download
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-2 mt-2 text-[10px] font-bold text-[#1f8c55] hover:text-[#25D366] transition-colors uppercase tracking-wider"
      >
        <FiDownload className="text-sm" /> Download {type}
      </a>
    );

    if (type === "image") {
      return (
        <div className="flex flex-col">
          <img
            src={msg.media_url}
            alt="media"
            className="mt-2 rounded-lg w-full max-h-80 object-contain shadow-sm border border-gray-100"
          />
          {downloadLink}
        </div>
      );
    }

    if (type === "video") {
      return (
        <div className="flex flex-col">
          <video controls className="mt-2 rounded-lg w-full max-h-80 shadow-sm border border-gray-100">
            <source src={msg.media_url} />
          </video>
          {downloadLink}
        </div>
      );
    }

    if (type === "audio") {
      return (
        <div className="flex flex-col">
          <audio controls className="mt-2 w-full max-w-[240px]">
            <source src={msg.media_url} />
          </audio>
          {downloadLink}
        </div>
      );
    }

    return (
      <a
        href={msg.media_url}
        download
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-3 p-3 mt-2 border border-gray-100 rounded-xl bg-gray-50/50 hover:bg-white hover:shadow-md transition-all group"
      >
        <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center group-hover:bg-orange-200 transition-colors">
          <FaRegFileAlt className="text-orange-600 text-lg" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-800 truncate">Document</p>
          <p className="text-[10px] text-gray-500 uppercase tracking-tighter">Click to download</p>
        </div>
        <FiDownload className="text-gray-400 group-hover:text-[#25D366] transition-colors text-lg" />
      </a>
    );
  };

  // Chat View Component
  const ChatView = () => (
    <div className="flex-1 flex flex-col bg-white h-full font-['Montserrat']">
      {selectedChat ? (
        <>
          {/* Chat Header */}
          <div className="px-6 py-4 bg-white border-b border-gray-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {isMobileView && (
                  <button
                    onClick={handleBackToChatList}
                    className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <FiArrowLeft className="text-gray-600 text-xl" />
                  </button>
                )}
                <div className="w-12 h-12 bg-gradient-to-br from-[#25D366] to-[#1f8c55] rounded-full flex items-center justify-center">
                  <FiUser className="text-white text-lg" />
                </div>
                <div>
                  <h2 className="font-semibold text-gray-900 text-lg font-['Montserrat']">
                    {selectedChat.contact_name || selectedChat.mobile_number}
                  </h2>
                  <div className="flex items-center space-x-1">
                    <p className="text-xs text-gray-500 font-['Montserrat']">
                      {selectedChat.contact_name
                        ? selectedChat.mobile_number
                        : ""}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Messages Area */}
          <div
            ref={messagesContainerRef}
            onScroll={handleScroll}
            className="flex-1 p-6 overflow-y-auto bg-gradient-to-b from-gray-50 to-white"
          >
            {loadingMessages ? (
              <div className="flex items-center justify-center h-full">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-10 h-10 border-4 border-[#25D366] border-t-transparent rounded-full animate-spin" />
                  <p className="text-xs text-gray-500 font-medium">
                    Loading messages…
                  </p>
                </div>
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <div className="bg-gray-100 p-6 rounded-2xl mb-4">
                  <BsWhatsapp className="text-gray-300 text-4xl" />
                </div>
                <p className="text-sm font-medium text-gray-500 font-['Montserrat']">
                  No messages yet
                </p>
                <p className="text-xs text-gray-400 mt-1 font-['Montserrat']">
                  Start the conversation
                </p>
              </div>
            ) : (
              <div className="max-w-4xl mx-auto space-y-4">
                {/* Loading indicator for older messages */}
                {loadingOld && (
                  <div className="flex justify-center py-2">
                    <div className="w-6 h-6 border-2 border-[#25D366] border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
                {Object.entries(groupedMessages).map(
                  ([dateKey, dayMessages]) => (
                    <div key={dateKey}>
                      {/* Date Separator */}
                      <div className="flex justify-center my-4">
                        <span className="px-4 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-full shadow-sm">
                          {getDateLabel(dateKey)}
                        </span>
                      </div>
                      {dayMessages.map((msg) => {
                        const isOutgoing = Number(msg.isSent) === 1;
                        return (
                          <div
                            key={msg.id}
                            className={`flex ${isOutgoing ? "justify-end" : "justify-start"
                              } mb-2`}
                          >
                            <div
                              className={`max-w-xs lg:max-w-md rounded-2xl px-4 py-3 shadow-sm ${isOutgoing
                                ? "bg-[#DCF8C8] text-black rounded-br-sm"
                                : "bg-white text-black border border-gray-200 rounded-bl-sm"
                                }${msg.media_url ? "p-2" : "px-4 py-3"}`}
                            >
                              {/* TEXT */}
                              {msg.message_type === "template" &&
                                msg.template_payload ? (
                                msg.message
                              ) : msg.media_url ? (
                                renderMediaMessage(msg)
                              ) : (
                                <p className="text-sm leading-relaxed whitespace-pre-wrap break-words text-black">
                                  {msg.message}
                                </p>
                              )}

                              <div className="flex items-center justify-end space-x-1 mt-2">
                                <span
                                  className={`text-xs ${isOutgoing ? "text-black" : "text-gray-500"
                                    }`}
                                >
                                  {formatTime(msg.timestamp)}
                                </span>

                                {isOutgoing && (
                                  <>
                                    {msg.status?.toLowerCase() === "failed" && (
                                      <BsExclamationCircle className="text-red-500 text-sm" />
                                    )}
                                    {msg.status?.toLowerCase() === "sent" && (
                                      <BsCheck className="text-gray-500 text-sm" />
                                    )}
                                    {msg.status?.toLowerCase() === "delivered" && (
                                      <BsCheck2All className="text-gray-500 text-sm" />
                                    )}
                                    {msg.status?.toLowerCase() === "read" && (
                                      <BsCheck2All className="text-[#34B7F1] text-sm" />
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ),
                )}
              </div>
            )}
          </div>

          {/* Conversation Footer */}
          <div className="px-6 py-4 bg-white border-t border-gray-200">
            <div className="text-center">
              <p className="text-xs text-gray-400 font-medium font-['Montserrat']">
                This conversation is secured with WhatsApp Business API
              </p>
            </div>
          </div>
        </>
      ) : (
        // Empty State
        <div className="flex-1 flex flex-col items-center justify-center bg-gradient-to-b from-gray-50 to-white p-8">
          <div className="bg-gradient-to-br from-[#25D366] to-[#1f8c55] p-8 rounded-3xl shadow-lg mb-6">
            <BsWhatsapp className="text-white text-5xl" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center font-['Montserrat']">
            Business Communication Console
          </h2>
          <p className="text-gray-500 text-center max-w-md leading-relaxed font-['Montserrat']">
            Select a conversation from the sidebar to view messages and manage
            customer communications.
          </p>
        </div>
      )}
    </div>
  );

  return (
    <div
      className={`bg-gray-50 font-['Montserrat'] ${isMobileView ? "h-screen" : "h-[calc(100vh-125px)]"
        }`}
    >
      {isMobileView ? (
        <div className="flex flex-col h-full">
          {showChat ? (
            <ChatView />
          ) : (
            <ChatList
              wabaNumber={wabaNumber}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              filteredChats={filteredChats}
              selectedChat={selectedChat}
              handleChatSelect={handleChatSelect}
              formatTime={formatTime}
              formatChatListTime={formatChatListTime}
              chatListRef={chatListRef}
              handleChatScroll={handleChatScroll}
              loadingChats={loadingChats}
              filter={filter}
              setFilter={setFilter}
            />
          )}
        </div>
      ) : (
        <div className="flex h-full rounded-2xl overflow-hidden shadow-lg border border-gray-200 m-4">
          <ChatList
            wabaNumber={wabaNumber}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            filteredChats={filteredChats}
            selectedChat={selectedChat}
            handleChatSelect={handleChatSelect}
            formatTime={formatTime}
            formatChatListTime={formatChatListTime}
            chatListRef={chatListRef}
            handleChatScroll={handleChatScroll}
            loadingChats={loadingChats}
            filter={filter}
            setFilter={setFilter}
          />
          <ChatView />
        </div>
      )}
    </div>
  );
};

export default HistoryLive;
